'use client';
import Image from 'next/image';
import * as React from 'react';
import { useTranslations } from 'next-intl';

import { Store } from '@/lib/types/customer/restaurant/StoreProfile';

import { Info, VerifiedIcon } from '@/components/ui/Icons';
import SvgStar from '@/components/ui/Icons/Star';
import useAppLocale from '@/hooks/useAppLocale';
import ImpressumModal from './ImpressumModal';
import STRINGS from '@/lib/utils/translation';

interface RestaurantMonogramProps {
  store: Store;
}

const RestaurantMonogram = ({ store }: RestaurantMonogramProps) => {
  const t = useTranslations();
  const [openInfoModal, setOpenInfoModal] = React.useState<boolean>(false);
  const { lang } = useAppLocale();
  const handleImpressumClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setOpenInfoModal(true);
  };

  const hasImpressum = !!store.meta?.[0].impressum?.[lang];

  return (
    <div className="container relative z-10 -mt-16">
      <div className="space-y-4 rounded-md bg-white px-6 py-10 shadow-lg sm:w-[344px]">
        <div className="flex items-start justify-start space-x-5">
          <div className="relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded">
            <Image
              src={
                store.media?.logo?.[0]?.thumb_url ?? '/res-logo-placeholder.png'
                // '/store-icon-placeholder.jpeg'
              }
              alt={store.name + 'logo'}
              fill
              className="shrink-0 object-cover"
            />
          </div>
          <div className="">
            <div className="store-name flex items-center justify-start space-x-2">
              <h1 className="font-bold text-title">{store.name}</h1>
              {store.features.ui_storefront_show_verified_mark_in_profile && (
                <div className="verification-icon">
                  <VerifiedIcon color="green" />
                </div>
              )}
              {hasImpressum && (
                <div
                  role="button"
                  tabIndex={0} // Add tabIndex to make the element focusable
                  className="flex flex-grow justify-end text-title transition-colors focus-within:border-none focus-within:outline-none hover:text-primary-700 focus:border-none focus:outline-none focus-visible:border-none focus-visible:outline-none"
                  onClick={(e) => handleImpressumClick(e)}
                >
                  <Info />
                </div>
              )}
            </div>
            {store.short_description && (
              <div className="">
                <span className="text-sm text-paragraph">
                  {store.short_description}
                </span>
              </div>
            )}
          </div>
        </div>
        {/* Review and availability */}
        <div className="reviews-availability flex items-center justify-start space-x-3">
          {store.avg_total_rating.counts.all > 0 && (
            <div className="review-rating flex items-center justify-start space-x-2">
              <div>
                <SvgStar color="#FFC107" />
              </div>
              <div className=" text-lg font-bold text-paragraph">
                {store.avg_total_rating?.formatted.all}
              </div>
              <div className="text-sm text-title">
                ({store.total_reviews} {t(STRINGS.COMMON + '.reviews')})
              </div>
            </div>
          )}
          <div className="availability text-xs">
            {store.allows_delivery && (
              <span className="hidden rounded-full border border-green-200 px-2 py-1 text-green-600">
                {t(STRINGS.SEARCH + '.delivery_available')}
              </span>
            )}
          </div>
        </div>
      </div>
      <ImpressumModal
        openInfoModal={openInfoModal}
        setOpenInfoModal={setOpenInfoModal}
        content={store.meta?.[0].impressum?.[lang]}
      />
    </div>
  );
};

export default RestaurantMonogram;
